import http from '@utils/http';

/**
 * GET /api/v1/projects/:projectId/tasks (project-wide tasks when sprints are disabled)
 */
export const getProjectTasks = (projectId, params) =>
    http.get(`/projects/${projectId}/tasks`, { params }).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.tasks)) return raw.tasks;
        return [];
    });

/**
 * GET /api/v1/projects/:projectId/sprints/:sprintId/tasks
 */
export const getProjectSprintTasks = (projectId, sprintId) =>
    http.get(`/projects/${projectId}/sprints/${sprintId}/tasks`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.tasks)) return raw.tasks;
        return [];
    });

/**
 * GET /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId
 * Fetch full sprint task details, including task attachments and related bugs.
 */
export const getProjectSprintTaskDetail = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}`
        : `/projects/${projectId}/tasks/${taskId}`;
    return http.get(url).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/sprints/tasks
 * @param {string} projectId
 * @param {{ sprintId: string, title: string, description?: object, type?: string, priority?: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON when no files; FormData multipart (same as discussion/Multer) when files attached.
 */
export const createProjectSprintTask = (projectId, body) => {
    const hasSprint = body instanceof FormData
        ? body.has('sprintId') && body.get('sprintId') !== 'no-sprint' && body.get('sprintId') !== ''
        : body?.sprintId && body.sprintId !== 'no-sprint';
    const url = hasSprint ? `/projects/${projectId}/sprints/tasks` : `/projects/${projectId}/tasks`;

    // The direct tasks API doesn't accept sprintId. Remove it before sending.
    let payload = body;
    if (!hasSprint) {
        if (body instanceof FormData) {
            payload = new FormData();
            for (const [key, value] of body.entries()) {
                if (key !== 'sprintId') payload.append(key, value);
            }
        } else {
            const { sprintId: _s, ...rest } = body;
            payload = rest;
        }
    }
    return http.post(url, payload).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId
 * @param {string} taskId - Sprint task `TaskId` (not internal `id`).
 * @param {{ title?: string, description?: object, type?: string, priority?: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON when no files; FormData multipart when files attached (same fields as POST create task).
 */
export const updateProjectSprintTask = (projectId, sprintId, taskId, body) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}`
        : `/projects/${projectId}/tasks/${taskId}`;
    return http.patch(url, body).then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId
 * @param {string} taskId - Sprint task id for API paths
 */
export const deleteProjectSprintTask = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}`
        : `/projects/${projectId}/tasks/${taskId}`;
    return http.delete(url).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/complete
 * @param {string} taskId - Sprint task `TaskId` (not internal `id`).
 */
export const completeProjectSprintTask = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/complete`
        : `/projects/${projectId}/tasks/${taskId}/complete`;
    return http.patch(url).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/todo
 * @param {string} taskId - Sprint task `TaskId` (not internal `id`).
 */
export const revertProjectSprintTaskToTodo = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/todo`
        : `/projects/${projectId}/tasks/${taskId}/todo`;
    return http.patch(url).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/sort-order
 * @param {string} projectId
 * @param {string} sprintId
 * @param {string} taskId
 * @param {{ sortOrder: number }} body - 1-based task position in Todo column
 */
export const sortProjectSprintTask = (projectId, sprintId, taskId, body) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/sort-order`
        : `/projects/${projectId}/tasks/${taskId}/sort-order`;
    return http.patch(url, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/assign
 * @param {{ assigneeId: string }} body - JSON body: `{ "assigneeId": "<user id>" }`
 */
export const assignProjectSprintTask = (projectId, sprintId, taskId, body) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/assign`
        : `/projects/${projectId}/tasks/${taskId}/assign`;
    return http.patch(url, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/resolve
 */
export const resolveProjectSprintTask = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/resolve`
        : `/projects/${projectId}/tasks/${taskId}/resolve`;
    return http.patch(url).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId/tasks/:taskId/revert-to-qa
 */
export const revertProjectSprintTaskToQA = (projectId, sprintId, taskId) => {
    const url = sprintId && sprintId !== 'no-sprint'
        ? `/projects/${projectId}/sprints/${sprintId}/tasks/${taskId}/revert-to-qa`
        : `/projects/${projectId}/tasks/${taskId}/revert-to-qa`;
    return http.patch(url).then((res) => res.data?.data ?? res.data);
};

